import app from 'flarum/forum/app';
import Modal from 'flarum/common/components/Modal';
import Button from 'flarum/common/components/Button';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

/**
 * CashBeeModal — runs a CashBee analysis (summary / insights / engagement)
 * for a discussion and renders the structured result in a clean block.
 *
 * attrs:
 *   discussion: Discussion model
 */
export default class CashBeeModal extends Modal {
  oninit(vnode) {
    super.oninit(vnode);

    this.discussion = this.attrs.discussion;
    this.mode = 'summary';
    this.loading = false;
    this.result = null;
    this.error = null;
    this.postsAnalyzed = null;
  }

  className() {
    return 'CashBeeModal Modal--large';
  }

  title() {
    return [m('i.icon.fas.fa-robot.CashBeeModal-titleIcon'), ' ', app.translator.trans('thecashbuzz-cashbee-ai.forum.modal.title')];
  }

  content() {
    const modes = [
      ['summary', 'fas fa-align-left'],
      ['insights', 'fas fa-lightbulb'],
      ['engagement', 'fas fa-comments'],
    ];

    return m('div.Modal-body', [
      m('div.CashBeeModal-tabs', modes.map(([mode, icon]) =>
        Button.component(
          {
            className: 'Button Button--link CashBeeModal-tab' + (this.mode === mode ? ' is-active' : ''),
            icon,
            disabled: this.loading,
            onclick: () => this.select(mode),
          },
          app.translator.trans(`thecashbuzz-cashbee-ai.forum.modes.${mode}`)
        )
      )),

      m('div.CashBeeModal-output', [this.renderOutput()]),

      m('div.CashBeeModal-footer.helpText', app.translator.trans('thecashbuzz-cashbee-ai.forum.modal.disclaimer')),
    ]);
  }

  renderOutput() {
    if (this.loading) {
      return m('div.CashBeeModal-loading', [
        LoadingIndicator.component({ display: 'block' }),
        m('p', app.translator.trans('thecashbuzz-cashbee-ai.forum.modal.loading')),
      ]);
    }

    if (this.error) {
      return m('div.CashBeeModal-error.Alert.Alert--error', this.error);
    }

    if (!this.result) {
      return m('div.CashBeeModal-empty', [
        m('p', app.translator.trans('thecashbuzz-cashbee-ai.forum.modal.intro')),
        Button.component(
          { className: 'Button Button--primary', icon: 'fas fa-robot', onclick: () => this.generate() },
          app.translator.trans('thecashbuzz-cashbee-ai.forum.modal.run')
        ),
      ]);
    }

    return [this.renderResult(), this.renderMeta()];
  }

  renderResult() {
    const r = this.result;

    switch (this.mode) {
      case 'summary':
        return m('div.CashBeeBlock', [
          this.section('summary_overview', r.summary && m('p', r.summary)),
          this.list('summary_points', r.keyPoints),
        ]);

      case 'insights':
        return m('div.CashBeeBlock', [
          this.list('insights_themes', r.themes),
          this.list('insights_tools', r.tools),
          this.list('insights_patterns', r.patterns),
        ]);

      case 'engagement':
        return m('div.CashBeeBlock', [
          this.list('engagement_questions', r.questions),
          this.list('engagement_prompts', r.prompts),
        ]);

      default:
        return null;
    }
  }

  renderMeta() {
    if (!this.postsAnalyzed) return null;

    return m('div.CashBeeBlock-meta.helpText',
      app.translator.trans('thecashbuzz-cashbee-ai.forum.modal.analyzed', { count: this.postsAnalyzed })
    );
  }

  section(key, body) {
    if (!body) return null;
    return m('div.CashBeeBlock-section', [
      m('h4', app.translator.trans(`thecashbuzz-cashbee-ai.forum.labels.${key}`)),
      body,
    ]);
  }

  list(key, items) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return m('div.CashBeeBlock-section', [
      m('h4', app.translator.trans(`thecashbuzz-cashbee-ai.forum.labels.${key}`)),
      m('ul', items.map((item) => m('li', String(item)))),
    ]);
  }

  select(mode) {
    if (this.mode === mode) return;
    this.mode = mode;
    this.result = null;
    this.error = null;
    this.postsAnalyzed = null;
  }

  generate() {
    this.loading = true;
    this.error = null;

    app
      .request({
        method: 'POST',
        url: app.forum.attribute('apiUrl') + '/cashbee/generate',
        body: { discussionId: this.discussion.id(), mode: this.mode },
      })
      .then((response) => {
        const attributes = (response && response.data && response.data.attributes) || {};
        this.result = attributes.result || {};
        this.postsAnalyzed = attributes.postsAnalyzed || null;
        this.loading = false;
        m.redraw();
      })
      .catch((e) => {
        this.loading = false;
        this.error = this.humanError(e);
        m.redraw();
      });
  }

  humanError(e) {
    const code = e && e.response && Array.isArray(e.response.errors) && e.response.errors[0] && e.response.errors[0].code;
    const key = ['not_configured', 'upstream_error', 'empty_response', 'invalid_json', 'empty_thread'].includes(code)
      ? code
      : 'generic';

    return app.translator.trans(`thecashbuzz-cashbee-ai.forum.errors.${key}`);
  }
}
